import { useState, useEffect } from 'react'
import { AuthProvider, useAuth } from './context/AuthContext'
// Admin pages
import DashboardPage from './pages/admin/DashboardPage'
import MemberManagementPage from './pages/admin/MemberManagementPage'
import SalespersonPage from './pages/admin/SalespersonPage'
import NewsPage from './pages/admin/NewsPage'
import AccountPage from './pages/admin/AccountPage'
// User pages
import UserDashboardPage from './pages/user/UserDashboardPage'
import ImageSourcePage from './pages/user/ImageSourcePage'
import DmAutoReplyPage from './pages/user/DmAutoReplyPage'
import CommentDmPage from './pages/user/CommentDmPage'
import CalendarPage from './pages/user/CalendarPage'
// Salesperson pages
import MySalespersonPage from './pages/salesperson/MySalespersonPage'
import ClientManagementPage from './pages/salesperson/ClientManagementPage'
import ChangePasswordPage from './pages/salesperson/ChangePasswordPage'
// Common pages
import LoginPage from './pages/common/LoginPage'
import RegisterPage from './pages/common/RegisterPage'
import TermsPage from './pages/common/TermsPage'
import PrivacyPolicyPage from './pages/common/PrivacyPolicyPage'

// Placeholder pages for upcoming features
function ContentStatsPage() {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-700 mb-4">bar_chart</span>
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">콘텐츠 통계</h2>
      <p className="text-slate-500 dark:text-slate-400">준비 중인 기능입니다.</p>
    </div>
  )
}

function PaymentPage() {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-700 mb-4">payments</span>
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">결제 관리</h2>
      <p className="text-slate-500 dark:text-slate-400">준비 중인 기능입니다.</p>
    </div>
  )
}

import PromptTemplatePage from './pages/admin/PromptTemplatePage'

function MainContent() {
  const { user, logout, loading } = useAuth()
  const isAdmin = user?.role === 'ADMIN' || user?.username === 'admin'
  const isSalesperson = user?.role === 'SALESPERSON'

  const [activeTab, setActiveTab] = useState(null)
  const [showRegister, setShowRegister] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [prevScreen, setPrevScreen] = useState('login')
  const [isNewUser, setIsNewUser] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // 역할별 기본 탭 (useEffect 없이 즉시 계산)
  const defaultTab = isAdmin ? 'admin-dashboard' : (isSalesperson ? 'my-dashboard' : 'user-dashboard')
  const effectiveTab = activeTab || defaultTab

  const userTabs = [
    { id: 'user-dashboard', label: '대시보드', icon: 'dashboard', component: UserDashboardPage },
    { id: 'automation', label: '자동화 설정', icon: 'settings_suggest', component: ImageSourcePage },
    { id: 'calendar', label: '캘린더', icon: 'calendar_month', component: CalendarPage },
    { id: 'dm-auto-reply', label: '자동 답장', icon: 'bolt', component: DmAutoReplyPage },
    { id: 'comment-dm', label: '댓글 DM', icon: 'forum', component: CommentDmPage },
  ]

  const adminTabs = [
    { id: 'admin-dashboard', label: '대시보드', icon: 'dashboard', component: DashboardPage },
    { id: 'members', label: '회원 관리', icon: 'group', component: MemberManagementPage },
    { id: 'salesperson', label: '영업사원 관리', icon: 'badge', component: SalespersonPage },
    { id: 'content-stats', label: '콘텐츠 통계', icon: 'bar_chart', component: ContentStatsPage },
    { id: 'payment', label: '결제 관리', icon: 'payments', component: PaymentPage },
    { id: 'prompt-templates', label: '프롬프트 관리', icon: 'edit_note', component: PromptTemplatePage },
  ]

  const salespersonTabs = [
    { id: 'my-dashboard', label: '내 실적', icon: 'analytics', component: MySalespersonPage },
    { id: 'clients', label: '고객 관리', icon: 'group', component: ClientManagementPage },
    { id: 'change-password', label: '비밀번호 변경', icon: 'lock', component: ChangePasswordPage },
  ]

  const tabs = isAdmin ? adminTabs : (isSalesperson ? salespersonTabs : userTabs)
  const ActiveComponent = tabs.find(tab => tab.id === effectiveTab)?.component

  if (loading) {
    return (
        <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center">
          <div className="text-slate-900 dark:text-white text-xl">로딩 중...</div>
        </div>
    )
  }

  const handleRegisterSuccess = () => {
    setIsNewUser(true)
    setShowRegister(false)
  }

  const handleViewTerms = (from) => {
    setPrevScreen(from || (showRegister ? 'register' : 'login'))
    setShowTerms(true)
    setShowPrivacy(false)
  }

  const handleViewPrivacy = (from) => {
    setPrevScreen(from || (showRegister ? 'register' : 'login'))
    setShowPrivacy(true)
    setShowTerms(false)
  }

  const handleBackFromLegal = () => {
    setShowTerms(false)
    setShowPrivacy(false)
    if (prevScreen === 'register') {
      setShowRegister(true)
    } else {
      setShowRegister(false)
    }
  }

  if (!user) {
    if (showTerms) {
      return <TermsPage onBack={handleBackFromLegal} />
    }
    if (showPrivacy) {
      return <PrivacyPolicyPage onBack={handleBackFromLegal} />
    }
    if (showRegister) {
      return <RegisterPage
        onSwitchToLogin={() => setShowRegister(false)}
        onRegisterSuccess={handleRegisterSuccess}
        onViewTerms={() => handleViewTerms('register')}
        onViewPrivacy={() => handleViewPrivacy('register')}
      />
    }
    return <LoginPage
      onSwitchToRegister={() => setShowRegister(true)}
      onViewTerms={() => handleViewTerms('login')}
      onViewPrivacy={() => handleViewPrivacy('login')}
    />
  }

  const roleLabel = isAdmin ? '관리자' : isSalesperson ? '영업사원' : '사용자'

  return (
      <div className="flex min-h-screen bg-background-light dark:bg-background-dark text-slate-900 dark:text-white">
        {/* 모바일 오버레이 */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* 사이드바 */}
        <aside className={`w-64 bg-white dark:bg-background-dark border-r border-slate-200 dark:border-slate-800 flex flex-col fixed h-full z-50 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
          {/* 로고 */}
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-primary flex items-center justify-center text-white">
                <span className="material-symbols-outlined">auto_awesome</span>
              </div>
              <div className="flex flex-col">
                <h1 className="text-slate-900 dark:text-white text-base font-bold leading-none">InstaAuto</h1>
                <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                  {roleLabel}
                </p>
              </div>
            </div>
            {/* 모바일 닫기 버튼 */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          {/* 네비게이션 */}
          <nav className="flex-1 px-4 space-y-1 mt-4 overflow-y-auto">
            {tabs.map(tab => (
                <button
                    key={tab.id}
                    onClick={() => {
                      if (window.__automationDirty && effectiveTab === 'automation' && tab.id !== 'automation') {
                        if (!window.confirm('저장하지 않은 변경사항이 있습니다.\n페이지를 나가시겠습니까?')) return;
                        window.__automationDirty = false;
                      }
                      setActiveTab(tab.id); setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg font-medium transition-colors ${
                        effectiveTab === tab.id
                            ? 'bg-primary/10 text-primary'
                            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                >
                  <span className={`material-symbols-outlined text-[20px] ${effectiveTab === tab.id ? 'fill-1' : ''}`}>
                    {tab.icon}
                  </span>
                  <p className="text-sm font-semibold">{tab.label}</p>
                </button>
            ))}
          </nav>

          {/* 하단 사용자 정보 */}
          <div className="p-4 border-t border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-3 p-2 mb-3">
              <div className="size-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-bold">
                {user.username?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">@{user.username}</p>
                <p className="text-[10px] text-primary font-bold">{roleLabel}</p>
              </div>
            </div>
            <button
                onClick={() => logout()}
                className="w-full py-2.5 bg-primary text-white rounded-lg font-bold text-sm hover:opacity-90 transition-opacity"
            >
              로그아웃
            </button>
          </div>
        </aside>

        {/* 메인 콘텐츠 */}
        <main className="flex-1 lg:ml-64 overflow-y-auto bg-background-light dark:bg-background-dark">
          {/* 모바일 상단 헤더 */}
          <div className="sticky top-0 z-30 lg:hidden bg-white dark:bg-background-dark border-b border-slate-200 dark:border-slate-800 px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <span className="material-symbols-outlined">menu</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="size-7 rounded-full bg-primary flex items-center justify-center text-white">
                <span className="material-symbols-outlined text-[16px]">auto_awesome</span>
              </div>
              <span className="font-bold text-sm">InstaAuto</span>
            </div>
            <div className="size-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-bold">
              {user.username?.charAt(0)?.toUpperCase()}
            </div>
          </div>

          <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
            {ActiveComponent && <ActiveComponent
              {...(effectiveTab === 'user-dashboard' ? { onNavigateToAutomation: () => setActiveTab('automation') } : {})}
            />}
          </div>
        </main>
      </div>
  )
}

function App() {
  const [hash, setHash] = useState(window.location.hash)

  useEffect(() => {
    const onHashChange = () => setHash(window.location.hash)
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  // 공개 페이지 (인증 불필요) - Facebook 앱 심사용 URL
  if (hash === '#/privacy') {
    return <PrivacyPolicyPage onBack={() => { window.location.hash = '' }} />
  }
  if (hash === '#/terms') {
    return <TermsPage onBack={() => { window.location.hash = '' }} />
  }

  return (
      <AuthProvider>
        <MainContent />
      </AuthProvider>
  )
}

export default App
